import React, { useEffect, useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';
import { ThemeInterface } from '../../interfaces/themeInterface';
import * as S from './AccessOverlay.style';

interface Dot {
  x: number;
  y: number;
  nx: number;
  ny: number;
}

interface BackdropPalette {
  bgBase: string;
  dot: string;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

const quantize = (value: number, steps: number) => {
  if (steps <= 1) return value;
  return Math.round(value * (steps - 1)) / (steps - 1);
};

const getPalette = (theme: ThemeInterface): BackdropPalette => {
  return {
    bgBase: theme.background,
    dot: theme.name === 'long' ? 'blue' : '#ffccd5',
  };
};

export const MarketMicrostructureBackdrop: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme() as ThemeInterface;
  const palette = useMemo(() => getPalette(theme), [theme.background, theme.name]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const maxDpr = 2;
    const frameInterval = 1000 / 24;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let spacing = 8;
    let baseDot = 1.8;
    let dots: Dot[] = [];
    let reducedMotion = prefersReducedMotion.matches;
    let rafId = 0;
    let lastFrame = 0;

    const buildDots = () => {
      dots = [];
      const cols = Math.ceil(width / spacing) + 2;
      const rows = Math.ceil(height / spacing) + 2;

      for (let row = -1; row < rows; row += 1) {
        for (let col = -1; col < cols; col += 1) {
          const x = col * spacing;
          const y = row * spacing;
          dots.push({
            x,
            y,
            nx: width > 0 ? x / width : 0,
            ny: height > 0 ? y / height : 0,
          });
        }
      }
    };

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = Math.max(1, Math.round(bounds.width));
      height = Math.max(1, Math.round(bounds.height));
      dpr = Math.min(window.devicePixelRatio || 1, maxDpr);

      spacing = Math.max(6, Math.min(10, Math.round(Math.min(width, height) / 90)));
      baseDot = Math.max(1.2, Math.min(2.2, spacing * 0.28));

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      buildDots();
      drawFrame(performance.now());
    };

    const drawBackground = () => {
      context.fillStyle = palette.bgBase;
      context.fillRect(0, 0, width, height);
    };

    const drawField = (time: number) => {
      const t = reducedMotion ? 0 : time * 0.00006;
      const c1x = 0.22 + Math.sin(t * 0.7) * 0.08;
      const c1y = 0.36 + Math.cos(t * 0.9) * 0.07;
      const c2x = 0.74 + Math.sin(t * 0.52 + 1.7) * 0.08;
      const c2y = 0.64 + Math.cos(t * 0.63 + 2.3) * 0.07;

      context.fillStyle = palette.dot;

      for (let i = 0; i < dots.length; i += 1) {
        const dot = dots[i];

        const dx1 = dot.nx - c1x;
        const dy1 = dot.ny - c1y;
        const dx2 = dot.nx - c2x;
        const dy2 = dot.ny - c2y;
        const d1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
        const d2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

        const waveA = Math.sin(dot.nx * 11.5 + dot.ny * 4.8 + t * 8.5);
        const waveB = Math.cos(dot.ny * 14.2 - dot.nx * 3.6 - t * 7.6);
        const waveC = Math.sin((dot.nx + dot.ny) * 18.4 + t * 4.1);
        const basin = 0.62 / (0.18 + d1 * 2.4) - 0.58 / (0.2 + d2 * 2.8);

        const field = waveA * 0.32 + waveB * 0.26 + waveC * 0.22 + basin;
        const normalized = clamp(field * 0.34 + 0.5, 0, 1);

        let density = smoothstep(0.22, 0.92, normalized);
        density = quantize(density, 6);

        if (density < 0.08) {
          continue;
        }

        const size = baseDot + density * spacing * 0.28;
        context.globalAlpha = 0.1 + density * 0.72;
        context.fillRect(dot.x - size / 2, dot.y - size / 2, size, size);
      }

      context.globalAlpha = 1;
    };

    const drawFrame = (time: number) => {
      context.clearRect(0, 0, width, height);
      drawBackground();
      drawField(time);
    };

    const tick = (time: number) => {
      if (!reducedMotion && time - lastFrame >= frameInterval) {
        drawFrame(time);
        lastFrame = time;
      }

      if (!reducedMotion) {
        rafId = window.requestAnimationFrame(tick);
      }
    };

    const stopAnimation = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
    };

    const startAnimation = () => {
      if (reducedMotion || document.hidden || rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(tick);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAnimation();
      } else {
        drawFrame(performance.now());
        startAnimation();
      }
    };

    const handleMotionPreference = () => {
      reducedMotion = prefersReducedMotion.matches;
      drawFrame(performance.now());

      if (reducedMotion) {
        stopAnimation();
      } else {
        startAnimation();
      }
    };

    resize();
    startAnimation();

    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (typeof prefersReducedMotion.addEventListener === 'function') {
      prefersReducedMotion.addEventListener('change', handleMotionPreference);
    } else {
      prefersReducedMotion.addListener(handleMotionPreference);
    }

    return () => {
      stopAnimation();
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (typeof prefersReducedMotion.removeEventListener === 'function') {
        prefersReducedMotion.removeEventListener('change', handleMotionPreference);
      } else {
        prefersReducedMotion.removeListener(handleMotionPreference);
      }
    };
  }, [palette]);

  return <S.BackgroundCanvas ref={canvasRef} aria-hidden="true" />;
};
